import React, { useMemo, useContext, useState } from "react";
import Bugsnag from "@bugsnag/js";
import { Client, cacheExchange, fetchExchange, Provider as UrqlProvider, CombinedError } from "urql";
import { AuthUtilities, authExchange } from "@urql/exchange-auth";

import { loginContext, getDefaultLoginValue } from "./containers/Login";
import { useInterval } from "../../components/src/common/Utils";
import { App } from "./App";

import { ContextProvider } from "../../components/src/contexts/ContextProvider";
import { TermsOfServicePage } from "./containers/TermsOfServicePage";
import { REACT_APP_API_URI } from "./env";
import { OPItemContextProvider } from "./contexts/OpItemContextProvider";

const BUGSNAG_IGNORED_GRAPHQL_CODES = new Set(["BAD_USER_INPUT", "FORBIDDEN"]);

export function shouldReportGraphqlErrorToBugsnag(error: CombinedError): boolean {
    if (error.networkError && error.graphQLErrors.length === 0) {
        return false;
    }

    const status = error.response?.status;
    if (typeof status === "number" && status >= 400 && status < 500) {
        return false;
    }

    if (
        error.graphQLErrors.length > 0 &&
        error.graphQLErrors.every((gqlError) => {
            const code = gqlError.extensions?.code;
            return typeof code === "string" && BUGSNAG_IGNORED_GRAPHQL_CODES.has(code);
        })
    ) {
        return false;
    }

    return true;
}

export const GqlClient = (): React.ReactElement => {
    const [pollInterval, setPollInterval] = useState(0);
    const { login, setLogin } = useContext(loginContext);

    const urqlClient = useMemo(() => {
        const urqlClient = new Client({
            url: REACT_APP_API_URI + "/graphql",
            exchanges: [
                cacheExchange,
                authExchange(async (utils: AuthUtilities) => {
                    return {
                        addAuthToOperation(operation) {
                            if (!login.token) return operation;
                            return utils.appendHeaders(operation, {
                                Authorization: `Bearer ${login.token}`
                            });
                        },
                        willAuthError() {
                            if (!login.loggedIn) {
                                return true;
                            }
                            const futureDate = new Date();
                            futureDate.setMinutes(futureDate.getMinutes() + 30);
                            const future = futureDate.toISOString();
                            if (future > login.expires) {
                                return true;
                            }
                            return false;
                        },
                        didAuthError(error, operation) {
                            const isAuthError =
                                error.response?.status === 401 ||
                                error.graphQLErrors.some((e) => e.extensions?.code === "FST_JWT_NO_AUTHORIZATION_IN_HEADER");

                            if (!isAuthError) {
                                // Enhanced logging for non-auth errors
                                console.group("🚨 Non-auth GraphQL Error");
                                console.error("Error:", error);

                                // Log operation details
                                console.log("Operation:", {
                                    query: operation.query?.loc?.source?.body || "No query source",
                                    variables: operation.variables,
                                    definitions: operation.query?.definitions,
                                    key: operation.key
                                });

                                // Log GraphQL errors in detail
                                if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                                    console.log("GraphQL Errors:");
                                    error.graphQLErrors.forEach((gqlError, index) => {
                                        console.group(`GraphQL Error ${index + 1}:`);
                                        console.error("Message:", gqlError.message);
                                        console.log("Locations:", gqlError.locations);
                                        console.log("Path:", gqlError.path);
                                        console.log("Extensions:", gqlError.extensions);
                                        console.groupEnd();
                                    });
                                }

                                // Log network error details
                                if (error.networkError) {
                                    console.group("Network Error:");
                                    console.error("Network Error:", error.networkError);
                                    console.log("Status:", error.response?.status);
                                    console.log("Status Text:", error.response?.statusText);
                                    console.log("Response Headers:", error.response?.headers);
                                    console.groupEnd();
                                }

                                // Log response details
                                if (error.response) {
                                    console.group("Response Details:");
                                    console.log("Status:", error.response.status);
                                    console.log("Status Text:", error.response?.statusText);
                                    console.log("Headers:", error.response.headers);
                                    console.log("Data:", error.response.data);
                                    console.groupEnd();
                                }

                                if (shouldReportGraphqlErrorToBugsnag(error)) {
                                    Bugsnag.notify(error, (event) => {
                                        event.addMetadata("graphql", {
                                            operation: operation.query?.loc?.source?.body,
                                            variables: operation.variables,
                                            definitions: operation.query?.definitions,
                                            graphQLErrors: error.graphQLErrors,
                                            networkError: error.networkError?.message,
                                            responseStatus: error.response?.status
                                        });
                                    });
                                }

                                console.groupEnd();
                            }

                            return isAuthError;
                        },
                        async refreshAuth() {
                            setLogin(getDefaultLoginValue());
                        }
                    };
                }),
                fetchExchange
            ]
            // fetchOptions: () => {
            //     return {
            //         headers: { authorization: login.token ? `Bearer ${login.token}` : "" }
            //     };
            // }
        });
        return urqlClient;
    }, [login.token, login.loggedIn, login.expires, setLogin]);

    useInterval(
        () => {
            if (pollInterval === 0) {
                setPollInterval(30000);
            }
            //console.log("token expires at: ", login.expires);
            if (login.loggedIn) {
                const futureDate = new Date();
                futureDate.setMinutes(futureDate.getMinutes() + 30);
                const future = futureDate.toISOString();
                if (future > login.expires) {
                    console.log("future: ", future);
                    console.log("token expires at: ", login.expires);
                    console.log("logout");
                    Bugsnag.leaveBreadcrumb("useInterval token expired");
                    setLogin(getDefaultLoginValue());
                }
            }
        },
        // Delay in milliseconds or null to stop it
        login.loggedIn ? pollInterval : null
    );

    return (
        <UrqlProvider value={urqlClient}>
            <loginContext.Provider value={{ login, setLogin }}>
                {login.acceptedTerms ? (
                    <ContextProvider>
                        <OPItemContextProvider>
                            <App />
                        </OPItemContextProvider>
                    </ContextProvider>
                ) : (
                    <TermsOfServicePage />
                )}
            </loginContext.Provider>
        </UrqlProvider>
    );
};
